import { FC, useEffect, useMemo } from 'react';

// Third party
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';

// Components
import startCase from 'lodash/startCase';
import { Iconify } from '@mgm/common/components';
import { BaseTooltip, GenericHeader } from '@mgm/common/components/base';
import { CustomTextField } from '@mgm/common/components/form';
import { PasswordValidation } from '@mgm/common/components/password';
import { BASE_COLORS } from '@mgm/common/utils';
import { WelcomeWrapper } from '@mgm/common/wrappers';
import { LanguageSelect } from '../../../components';
import {
  EmailField,
  PasswordField,
  TCreateEditForm,
  TFormInputs,
} from '../../../components/form';

// Hooks
import { usePasswordValidation, useValidation } from '../../../hooks/useValidation';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { useQueryParams } from '../../../hooks/useCommon';
import { useRegister } from '../../../hooks/api/useOnboarding';

// Redux-toolkit
import { getPasswordValidation } from '../../../store/common/selectors';
import { onboardingActions } from '../../../store/onboarding/slice';

// Types
import { TRegisterRequest } from '../../../types/requests';
import { AppPricingNav } from './AppPricingNav';

type TRegister = {
  handleStartTour: () => void
};

const Register: FC<TRegister> = ({ handleStartTour }) => {
  const { t } = useTranslation(['welcome', 'inputs', 'common']);
  const passwordValid = useAppSelector(getPasswordValidation);

  const {
    isEightCharacter,
    isSpecialCharacter,
    isLowercaseChar,
    isMaxCharacter,
    isUppercaseChar,
  } = useValidation();

  const { validationItems } = usePasswordValidation();

  const query = useQueryParams();
  const dispatch = useAppDispatch();

  const { mutateAsync: registerMutate, isLoading } = useRegister();

  useEffect(() => {
    sessionStorage.clear();
  }, []);

  const methods = useForm<TCreateEditForm>({
    mode: 'all',
    defaultValues: {
      email: query.get('ownerEmail') || '',
      fullName: query.get('ownerName') || '',
    },
  });

  const registerToPlatform = async (formData: Pick<TFormInputs, 'password' | 'email' | 'fullName'>) => {
    const newUser = {
      ...formData,
      userName: formData.fullName,
      shopName: query.get('shopName'),
    } as TRegisterRequest;

    dispatch(onboardingActions.setRegisteredUser(newUser));

    await registerMutate(newUser);

    const message = t('welcomeMsg', { ns: 'common', userName: startCase(query.get('ownerName') || '') });

    window.$crisp.push(['do', 'message:show', ['text', message]]);
  };

  const isFormValid = useMemo(() => {
    const isPasswordValid = Object.values(passwordValid).every(
      (value) => value || value === null,
    );

    return methods.formState.isValid && isPasswordValid;
  }, [methods.formState.isValid, passwordValid]);

  return (
    <WelcomeWrapper>
      <Stack justifyContent="flex-end" spacing={1.5} direction="row">
        <BaseTooltip tooltipText={t('clockHereToStartAGuided', { ns: 'common' })}>
          <Box
            sx={{
              cursor: 'pointer',
            }}
            alignItems="center"
            display="flex"
            onClick={handleStartTour}
          >
            <Iconify icon="cuida:help-outline" width={24} sx={{ color: BASE_COLORS.primary }} />
          </Box>
        </BaseTooltip>
        <LanguageSelect />
      </Stack>
      <GenericHeader
        titleProps={{
          children: t('createAnAccount'),
        }}
        subtitleProps={{
          children: t('trialText'),
          fontSize: 14,
        }}
      />
      <form
        noValidate
        onSubmit={methods.handleSubmit(registerToPlatform)}
      >
        <Stack spacing={3}>
          <FormProvider {...methods}>
            <div id="register-full-name">
              <CustomTextField
                placeholder={t('enterName', { ns: 'inputs' })}
                isRequiredField
                labelText={t('fullName', { ns: 'inputs' })}
                name="fullName"
              />
            </div>
            <div id="email-field">
              <EmailField />
            </div>
            <div id="register-password">
              <PasswordField
                isCheckValidation
                rules={{
                  isEightCharacter,
                  isSpecialCharacter,
                  isLowercaseChar,
                  isUppercaseChar,
                  isMaxCharacter,
                }}
              />
            </div>
            <div id="password-validation">
              <PasswordValidation validationItems={validationItems} />
            </div>
          </FormProvider>
        </Stack>
        <Box paddingY={2}>
          <LoadingButton
            id="create-button"
            fullWidth
            type="submit"
            loading={isLoading}
            variant="contained"
            disabled={!isFormValid}
            sx={{ my: 2 }}
          >
            {t('createAccount')}
          </LoadingButton>
        </Box>
      </form>
      <AppPricingNav />
    </WelcomeWrapper>
  );
};

export {
  Register,
};
